import { useState, useEffect, useCallback, useRef } from "react";
import { Editor } from "@tiptap/core";
import { TableToolbarPosition } from "./tableToolbar.types";

interface UseTableToolbarReturn {
  isVisible: boolean;
  position: TableToolbarPosition;
  showToolbar: () => void;
  hideToolbar: () => void;
}

export const useTableToolbar = (editor: Editor | null): UseTableToolbarReturn => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<TableToolbarPosition>({ top: 0, left: 0 });
  const lastTableElement = useRef<HTMLTableElement | null>(null);

  const calculatePosition = useCallback(() => {
    if (!editor) return;

    const { selection } = editor.state;
    const { $from } = selection;

    // Check if we're inside a table
    let tableNode = null;
    let tablePos = -1;

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "table") {
        tableNode = node;
        tablePos = $from.before(depth);
        break;
      }
    }

    if (!tableNode || tablePos < 0) {
      setIsVisible(false);
      lastTableElement.current = null;
      return;
    }

    // Get the DOM element for the table
    const domAtPos = editor.view.domAtPos(tablePos);
    let tableElement: HTMLTableElement | null = null;

    if (domAtPos.node instanceof HTMLTableElement) {
      tableElement = domAtPos.node;
    } else if (domAtPos.node.nodeType === Node.ELEMENT_NODE) {
      tableElement = (domAtPos.node as Element).querySelector("table");
    } else if (domAtPos.node.parentElement) {
      tableElement = domAtPos.node.parentElement.closest("table");
    }

    if (!tableElement) {
      // Try to find the table from selection
      const selectionNode = editor.view.domAtPos(selection.from).node;
      if (selectionNode instanceof Node) {
        const element = selectionNode instanceof Element
          ? selectionNode
          : selectionNode.parentElement;
        tableElement = element?.closest("table") ?? null;
      }
    }

    if (!tableElement) {
      setIsVisible(false);
      lastTableElement.current = null;
      return;
    }

    lastTableElement.current = tableElement;

    // Calculate position relative to the table
    const tableRect = tableElement.getBoundingClientRect();
    const editorElement = editor.view.dom.closest(".tiptap")?.parentElement;

    if (!editorElement) {
      setIsVisible(false);
      return;
    }

    const editorRect = editorElement.getBoundingClientRect();
    const scrollTop = editorElement.scrollTop;

    // Position toolbar above the table, centered horizontally
    const top = tableRect.top - editorRect.top + scrollTop - 48;
    const left = tableRect.left - editorRect.left + tableRect.width / 2;

    setPosition({ top, left });
    setIsVisible(true);
  }, [editor]);

  const showToolbar = useCallback(() => {
    calculatePosition();
  }, [calculatePosition]);

  const hideToolbar = useCallback(() => {
    setIsVisible(false);
    lastTableElement.current = null;
  }, []);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      // Check if cursor is inside a table
      const { selection } = editor.state;
      const { $from } = selection;

      let isInTable = false;
      for (let depth = $from.depth; depth > 0; depth--) {
        if ($from.node(depth).type.name === "table") {
          isInTable = true;
          break;
        }
      }

      if (isInTable) {
        calculatePosition();
      } else {
        hideToolbar();
      }
    };

    const handleTransaction = () => {
      // Recalculate position on transaction (e.g., after adding/removing rows/columns)
      if (isVisible) {
        setTimeout(calculatePosition, 10);
      }
    };

    editor.on("selectionUpdate", handleSelectionUpdate);
    editor.on("transaction", handleTransaction);

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
      editor.off("transaction", handleTransaction);
    };
  }, [editor, calculatePosition, hideToolbar, isVisible]);

  return {
    isVisible,
    position,
    showToolbar,
    hideToolbar,
  };
};
